import { useEffect, useState } from "react";
import { API } from "../lib/api";
import axios from "axios";
import { Button } from "./ui/button";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { YouTubeBrandIcon } from "./YouTubeIcon";
import { HeroPreview } from "./HeroPreview";
import { t, getLang } from "../i18n";
import { planPriceLabel } from "../lib/planPrice";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import {
  ArrowRight, PlayCircle, ShieldCheck, Check, Minus, Sparkles, X,
  Layers, Zap, Radio, Twitch, MessagesSquare, Sheet, Headphones,
  UserPlus, LayoutTemplate, Link2, Quote,
} from "lucide-react";

const ICONS = {
  layers: Layers, zap: Zap, radio: Radio, youtube: YouTubeBrandIcon, twitch: Twitch,
  messages: MessagesSquare, sheet: Sheet, headphones: Headphones,
  user: UserPlus, layout: LayoutTemplate, link: Link2,
};
const Icon = ({ name, className }) => { const C = ICONS[name] || Sparkles; return <C className={className} />; };
const abs = (u) => (u && u.startsWith("/api/") ? `${API}${u.replace(/^\/api/, "")}` : u);

const ytId = (u) => { const m = (u || "").match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/))([\w-]{11})/); return m ? m[1] : null; };
const vimeoId = (u) => { const m = (u || "").match(/vimeo\.com\/(?:video\/)?(\d+)/); return m ? m[1] : null; };

function DemoMedia({ url }) {
  const yt = ytId(url), vi = vimeoId(url);
  if (yt) return <div className="aspect-video w-full"><iframe src={`https://www.youtube.com/embed/${yt}`} title={t("Demonstração")} className="w-full h-full rounded-md" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;
  if (vi) return <div className="aspect-video w-full"><iframe src={`https://player.vimeo.com/video/${vi}`} title={t("Demonstração")} className="w-full h-full rounded-md" allowFullScreen /></div>;
  if (/\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(url)) return <video src={abs(url)} controls autoPlay loop muted playsInline className="w-full rounded-md" />;
  if (/\.(gif|png|jpe?g|webp|avif)(\?|#|$)/i.test(url)) return <img src={abs(url)} alt={t("Demonstração")} className="w-full rounded-md" />;
  return <div className="aspect-video w-full"><iframe src={url} title={t("Demonstração")} className="w-full h-full rounded-md" allowFullScreen /></div>;
}

const Heading = ({ title, subtitle }) => (
  <div className="text-center max-w-2xl mx-auto mb-12">
    <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{title}</h2>
    {subtitle && <p className="mt-3 text-muted-foreground text-base">{subtitle}</p>}
  </div>
);

function DemoModal({ open, onClose, url }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-card border-border" data-testid="demo-modal">
        <DialogTitle className="sr-only">{t("Demonstração do LiveGrafX")}</DialogTitle>
        <DialogDescription className="sr-only">{t("Prévia do fluxo: subir mídia, editar textos e usar a URL no OBS.")}</DialogDescription>
        <button onClick={onClose} className="absolute right-3 top-3 z-10 text-muted-foreground hover:text-foreground" data-testid="demo-modal-close"><X className="h-5 w-5" /></button>
        <div className="p-6">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-4">{t("demonstração · do editor ao ar")}</div>
          {url ? <DemoMedia url={url} /> : (
            <div className="scale-90 origin-top"><HeroPreview /></div>
          )}
          <p className="text-sm text-muted-foreground mt-4">{t("Suba sua mídia, edite os textos, copie a URL e cole no OBS como Browser Source. Tudo atualiza em tempo real.")}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Hero({ d, onLogin, onDemo }) {
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-8" data-testid="home-hero">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10 items-center">
        <div>
          {d.badge && <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-primary mb-6"><span className="h-2 w-2 rounded-full bg-primary live-dot" />{d.badge}</div>}
          <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {d.titulo} {d.titulo_destaque && <span className="text-primary">{d.titulo_destaque}</span>}.
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl">{d.subtitulo}</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <GoogleSignInButton size="lg" data-testid="hero-login-btn" onClick={onLogin}>{d.cta_primario || t("Começar com Google")}</GoogleSignInButton>
            {d.cta_secundario && <Button data-testid="hero-demo-btn" onClick={onDemo} variant="secondary" className="rounded-sm h-12 px-6 text-base"><PlayCircle className="mr-2 h-4 w-4" />{d.cta_secundario}</Button>}
          </div>
          {d.linha_confianca && <p className="mt-5 text-xs text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" />{d.linha_confianca}</p>}
        </div>
        <HeroPreview />
      </div>
    </section>
  );
}

function ProvaSocial({ d }) {
  const hasLogos = d.modo === "logos" && (d.logos || []).length > 0;
  const items = hasLogos ? d.logos : (d.contadores || []);
  if (items.length === 0) return null;
  return (
    <section className="border-y border-border/40 bg-card/30" data-testid="home-prova">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {d.titulo && <p className="text-center text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-8">{d.titulo}</p>}
        {hasLogos ? (
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {d.logos.map((l, i) => (l.url ? <img key={i} src={abs(l.url)} alt={l.nome} loading="lazy" className="h-8 w-auto opacity-70 hover:opacity-100 transition-opacity" /> : <span key={i} className="text-lg font-display font-bold text-muted-foreground">{l.nome}</span>))}
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-12 sm:gap-20">
            {d.contadores.map((c, i) => (
              <div key={i} className="text-center min-w-[110px]" data-testid={`counter-${i}`}>
                <div className="font-display font-extrabold text-4xl text-primary">{c.numero}</div>
                <div className="text-sm text-muted-foreground mt-1">{c.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Recursos({ d }) {
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-20" data-testid="home-recursos">
      <Heading title={d.titulo} subtitle={d.subtitulo} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {(d.cards || []).map((f, i) => (
          <div key={i} className="border border-border/60 bg-card rounded-sm p-6 hover:border-primary/50 transition-colors duration-200" data-testid={`feature-card-${i}`}>
            <Icon name={f.icone} className="h-6 w-6 text-white mb-4" />
            <h3 className="font-display font-bold text-base mb-2">{f.titulo}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{f.descricao}</p>
          </div>
        ))}
      </div>
      {(d.destaque_imagem || d.destaque_titulo) && (
        <div className="mt-8 grid lg:grid-cols-2 gap-8 items-center rounded-xl border border-border/60 bg-card overflow-hidden" data-testid="home-destaque">
          <div className="p-8 md:p-10 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-primary mb-4"><span className="h-2 w-2 rounded-full bg-primary live-dot" />{t("ao vivo")}</div>
            <h3 className="font-display font-extrabold text-2xl md:text-3xl">{d.destaque_titulo}</h3>
            <p className="mt-3 text-muted-foreground">{d.destaque_descricao}</p>
          </div>
          {d.destaque_imagem && <div className="order-1 lg:order-2 h-full"><img src={abs(d.destaque_imagem)} alt={d.destaque_titulo} loading="lazy" className="w-full h-full object-cover min-h-[240px]" /></div>}
        </div>
      )}
    </section>
  );
}

function ComoFunciona({ d }) {
  return (
    <section className="bg-card/30 border-y border-border/40" data-testid="home-como-funciona">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <Heading title={d.titulo} subtitle={d.subtitulo} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {(d.passos || []).map((p, i) => (
            <div key={i} className="relative" data-testid={`step-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center font-display font-bold">{i + 1}</span>
                <Icon name={p.icone} className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="font-display font-bold text-base mb-1">{p.titulo}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{p.descricao}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Planos({ d, onLogin }) {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/plans/landing`, { headers: { "X-Lang": getLang() } }).then(({ data }) => setData(data)).catch(() => {}); }, []);
  const grouped = () => {
    const order = [], b = {};
    for (const r of (data?.recursos || [])) { const c = r.categoria || t("Outros"); if (!b[c]) { b[c] = []; order.push(c); } b[c].push(r); }
    return order.flatMap((c) => b[c]);
  };
  const cell = (pid, rid) => ((data?.valores?.[pid] || {})[rid]) || {};
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-20" data-testid="home-planos">
      <Heading title={d.titulo} subtitle={d.subtitulo} />
      {!data ? <div className="flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div> : (
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {data.planos.map((p, idx) => (
            <div key={p.id} className={`rounded-xl border p-6 flex flex-col ${idx === 1 ? "border-primary shadow-[0_0_40px_-12px_var(--primary)]" : "border-border/60"} bg-card`} data-testid={`home-plan-${p.id}`}>
              {idx === 1 && <span className="self-start text-[10px] font-bold uppercase tracking-widest text-primary flex items-center gap-1 mb-2"><Sparkles className="h-3 w-3" />Popular</span>}
              <h3 className="font-display font-extrabold text-2xl">{p.nome}</h3>
              <div className="mt-2 mb-5"><span className="text-4xl font-extrabold">{planPriceLabel(p)}</span><span className="text-muted-foreground text-sm">{t("/mês")}</span></div>
              <ul className="space-y-2 flex-1">
                {grouped().map((r) => { const c = cell(p.id, r.id); if (r.tipo === "numero") return <li key={r.id} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-emerald-500 flex-shrink-0" /><span>{c.valor_numero ?? 0} · {r.nome_exibicao}</span></li>; return <li key={r.id} className={`flex items-center gap-2 text-sm ${c.valor_boolean ? "" : "text-muted-foreground/50"}`}>{c.valor_boolean ? <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" /> : <Minus className="h-4 w-4 flex-shrink-0" />}<span>{r.nome_exibicao}</span></li>; })}
              </ul>
              <Button className="w-full mt-6 rounded-md" variant={idx === 1 ? "default" : "secondary"} onClick={() => { const dest = p.cta_url && p.cta_url.trim(); if (dest) window.open(dest, dest.startsWith("http") ? "_blank" : "_self"); else onLogin(); }} data-testid={`home-plan-cta-${p.id}`}>{t("Começar com {nome}", { nome: p.nome })}</Button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Depoimentos({ d }) {
  if (!d.itens || d.itens.length === 0) return null;
  return (
    <section className="bg-card/30 border-y border-border/40" data-testid="home-depoimentos">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <Heading title={d.titulo} />
        <div className="grid md:grid-cols-3 gap-5">
          {d.itens.map((dep, i) => (
            <div key={i} className="rounded-xl border border-border/60 bg-card p-6" data-testid={`testimonial-${i}`}>
              <Quote className="h-6 w-6 text-primary/50 mb-3" />
              <p className="text-sm leading-relaxed">{dep.texto}</p>
              <div className="mt-4 flex items-center gap-3">
                {dep.avatar ? <img src={abs(dep.avatar)} alt={dep.nome} loading="lazy" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-sm">{(dep.nome || "?").charAt(0)}</div>}
                <div><div className="text-sm font-semibold">{dep.nome}</div><div className="text-xs text-muted-foreground">{dep.canal}</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ({ d }) {
  if (!d.itens || d.itens.length === 0) return null;
  return (
    <section className="max-w-3xl mx-auto px-6 md:px-10 py-20" data-testid="home-faq">
      <Heading title={d.titulo} />
      <Accordion type="single" collapsible className="w-full">
        {d.itens.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
            <AccordionTrigger className="text-left font-display font-semibold">{f.pergunta}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground">{f.resposta}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      {d.ver_mais_url && (
        <div className="text-center mt-8">
          <a href={d.ver_mais_url} className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1" data-testid="faq-ver-mais">{d.ver_mais_texto || t("Ver todas as perguntas frequentes")} <ArrowRight className="h-4 w-4" /></a>
        </div>
      )}
    </section>
  );
}

function CtaFinal({ d, onLogin }) {
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-10 py-20" data-testid="home-cta-final">
      <div className="rounded-2xl border border-primary/40 bg-gradient-to-b from-primary/10 to-transparent p-10 md:p-16 text-center">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight max-w-2xl mx-auto">{d.titulo}</h2>
        {d.subtitulo && <p className="mt-4 text-muted-foreground text-base">{d.subtitulo}</p>}
        <GoogleSignInButton size="lg" onClick={onLogin} className="mt-8" data-testid="cta-final-btn">{d.cta || t("Começar com Google")}</GoogleSignInButton>
      </div>
    </section>
  );
}

export default function HomeSections({ secoes, onLogin }) {
  const [demo, setDemo] = useState(false);
  const [demoUrl, setDemoUrl] = useState("");
  const openDemo = (url) => { setDemoUrl(url || ""); setDemo(true); };
  const visible = (secoes || []).filter((s) => s.visivel !== false);
  return (
    <>
      {visible.map((s) => {
        const d = s.dados || {};
        switch (s.tipo) {
          case "hero": return <Hero key={s.id} d={d} onLogin={onLogin} onDemo={() => openDemo(d.demo_url)} />;
          case "prova_social": return <ProvaSocial key={s.id} d={d} />;
          case "recursos": return <Recursos key={s.id} d={d} />;
          case "como_funciona": return <ComoFunciona key={s.id} d={d} />;
          case "planos": return <Planos key={s.id} d={d} onLogin={onLogin} />;
          case "depoimentos": return <Depoimentos key={s.id} d={d} />;
          case "faq": return <FAQ key={s.id} d={d} />;
          case "cta_final": return <CtaFinal key={s.id} d={d} onLogin={onLogin} />;
          default: return null;
        }
      })}
      <DemoModal open={demo} onClose={() => setDemo(false)} url={demoUrl} />
    </>
  );
}
